import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface ApprovalModalsProps {
  showDeclineModal: boolean;
  setShowDeclineModal: (v: boolean) => void;
  declineReason: string;
  setDeclineReason: (v: string) => void;
  handleDecline: () => void;
  actionLoading: boolean;

  showMoreInfoModal: boolean;
  setShowMoreInfoModal: (v: boolean) => void;
  moreInfoRequired: string;
  setMoreInfoRequired: (v: string) => void;
  selectedQuestionnaires: number[];
  setSelectedQuestionnaires: (v: number[]) => void;
  questionnaires: Array<{ id: number; dataset_name?: string; status: string }>;
  handleRequestMoreInfo: () => void;

  showApproveModal: boolean;
  setShowApproveModal: (v: boolean) => void;
  approvalLevel: '1' | '2';
  setApprovalLevel: (v: '1' | '2') => void;
  approvalComment: string;
  setApprovalComment: (v: string) => void;
  handleApprove: () => void;
}

/**
 * Modal dialogs used in the Supplier Approval Wizard:
 * - Decline Modal: reason textarea
 * - More Information Modal: questionnaire selection + description textarea
 * - Approve Modal: approval level radio group + comments textarea
 */
export default function ApprovalModals({
  showDeclineModal,
  setShowDeclineModal,
  declineReason,
  setDeclineReason,
  handleDecline,
  actionLoading,
  showMoreInfoModal,
  setShowMoreInfoModal,
  moreInfoRequired,
  setMoreInfoRequired,
  selectedQuestionnaires,
  setSelectedQuestionnaires,
  questionnaires,
  handleRequestMoreInfo,
  showApproveModal,
  setShowApproveModal,
  approvalLevel,
  setApprovalLevel,
  approvalComment,
  setApprovalComment,
  handleApprove,
}: ApprovalModalsProps) {
  return (
    <>
      {/* Decline Modal */}
      <Dialog open={showDeclineModal} onOpenChange={setShowDeclineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reason for Decline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Please provide a reason for declining..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDecline}
              disabled={!declineReason.trim() || actionLoading}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* More Information Modal */}
      <Dialog open={showMoreInfoModal} onOpenChange={setShowMoreInfoModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>More Information Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select questionnaires requiring more information:</Label>
              {questionnaires.filter(q => q.status === 'submitted' || q.status === 'under_review').map((q) => (
                <div key={q.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`q-${q.id}`}
                    checked={selectedQuestionnaires.includes(q.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedQuestionnaires([...selectedQuestionnaires, q.id]);
                      } else {
                        setSelectedQuestionnaires(selectedQuestionnaires.filter(id => id !== q.id));
                      }
                    }}
                  />
                  <label htmlFor={`q-${q.id}`} className="text-sm">
                    {q.dataset_name || 'Questionnaire'}
                  </label>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Further information required:</Label>
              <Textarea
                placeholder="Please describe what additional information is needed..."
                value={moreInfoRequired}
                onChange={(e) => setMoreInfoRequired(e.target.value)}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoreInfoModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestMoreInfo}
              disabled={!moreInfoRequired.trim() || selectedQuestionnaires.length === 0 || actionLoading}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Request Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Approval Level</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Please select the appropriate approval level and add any comments if necessary.
            </p>
            <RadioGroup value={approvalLevel} onValueChange={(v: string) => setApprovalLevel(v as '1' | '2')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="approval-1" />
                <Label htmlFor="approval-1" className="font-semibold">
                  Approved - No issues
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="2" id="approval-2" />
                <Label htmlFor="approval-2" className="font-semibold">
                  Approved - With concerns
                </Label>
              </div>
            </RadioGroup>
            <div className="space-y-2">
              <Label>Additional Comments:</Label>
              <Textarea
                placeholder="Add any comments..."
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
